// YERİ: src/app/domains/website/sections/mkt-price.ts
//
// Vitrindeki hizmet fiyatlarının TEK biçimlendiricisi. `mkt-services` (özet kart)
// ve `mkt-service-catalog` (tam liste) aynı sayıyı aynı şekilde yazsın diye ortak.
//
// Neden var: fiyatlar bir dönem CMS metninin içine ELLE yazılıyordu ("Logo design
// — $350"). 2026-08-07 fiyat revizyonundan sonra sayfadaki her rakam yanlıştı ve
// bunu kimse fark etmedi, çünkü yanlış bir sayı doğru bir sayı gibi görünür.
// Artık rakam veritabanından gelir; buradaki iş yalnız onu okunur hâle getirmek.
//
// TUTAR veriden, BİRİM SÖZCÜĞÜ CMS'ten: "19" bir olgu, "/ay" bir çeviridir.

/** Birim sözcükleri — CMS bloğunda sayfayla birlikte çevrilir. */
export interface PriceLabels {
  /** Fiyat öneki: "from" / "à partir de" / "desde". */
  from: string;
  month: string;
  hour: string;
  page: string;
  clip: string;
  language: string;
  /** Ücretsiz DEĞİL, pakete dahil: "Included in package". */
  included: string;
}

export const DEFAULT_PRICE_LABELS: PriceLabels = {
  from: 'from',
  month: '/mo',
  hour: '/hr',
  page: '/page',
  clip: '/clip',
  language: '/language',
  included: 'Included',
};

/**
 * Para birimi sembolü Intl'den gelir — sabit '$' yazmak yanlış olurdu: aynı tutar
 * CAD, USD ve EUR'da farklı yazılır ve Fransızcada sembol SONA gelir (199 $).
 * `narrowSymbol` "CA$199" yerine "$199" verir; para biriminin KODU dipnotta
 * ayrıca gösterilir, o yüzden belirsizlik kalmaz.
 */
export function formatMoney(amount: number, currency: string, locale: string): string {
  const safeCurrency = (currency || '').trim().toUpperCase();
  if (!safeCurrency) return String(Math.round(amount));

  try {
    return new Intl.NumberFormat(locale || 'en', {
      style: 'currency',
      currency: safeCurrency,
      currencyDisplay: 'narrowSymbol',
      // Vitrin fiyatları tam sayı; "199,00 $" pazarlama metninde gürültü.
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // Bilinmeyen para birimi kodu Intl'i patlatır — sayfayı düşürmektense
    // kodu tutarın yanına yaz.
    return `${Math.round(amount)} ${safeCurrency}`;
  }
}

/** "/ay", "/saat"… — bilinmeyen tip için ek yazılmaz, uydurulmaz. */
export function unitSuffix(priceType: string | null | undefined, labels: PriceLabels): string {
  switch ((priceType || '').trim().toLowerCase()) {
    case 'month': return labels.month;
    case 'hour': return labels.hour;
    case 'page': return labels.page;
    case 'clip': return labels.clip;
    case 'language': return labels.language;
    default: return '';
  }
}

/**
 * Bir kalemin ekranda görünecek fiyatı. Fiyatsız kalem "included" yazar —
 * "$0" yazmak kaldırılmış ücretsiz plan iddiasını geri getirirdi.
 */
export function formatItemPrice(
  item: { price?: number | null; priceType?: string | null; includedInPackage: boolean },
  currency: string,
  locale: string,
  labels: PriceLabels,
): string {
  if (item.includedInPackage || !item.price || item.price <= 0) return labels.included;
  return formatMoney(item.price, currency, locale) + unitSuffix(item.priceType, labels);
}

/** Kategori kartındaki "from $39" / "à partir de 19 $/mois". */
export function formatFromPrice(
  category: { fromPrice?: number | null; fromPriceType?: string | null },
  currency: string,
  locale: string,
  labels: PriceLabels,
): string {
  if (!category.fromPrice || category.fromPrice <= 0) return '';
  return formatMoney(category.fromPrice, currency, locale) + unitSuffix(category.fromPriceType, labels);
}

/**
 * CMS metnindeki yer tutucuları veriyle doldurur: `{count}`, `{total}`,
 * `{modules}`, `{currency}`.
 *
 * Neden yer tutucu: "11 services" cümlesindeki SAYI bir olgu, çevresindeki
 * sözcük bir çeviri. İkisini ayırmazsak ya sayı elle yazılır (bayatlar) ya da
 * cümle çevrilemez. CMS'e "{count} services" yazılır, sayıyı veri koyar.
 */
export function fillTokens(text: string, values: Record<string, string | number>): string {
  if (!text) return '';
  return text.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

/**
 * Çok satırlı CMS alanlarını (madde listeleri, güven satırları) satırlara böler.
 *
 * GERÇEK satır sonu ile LİTERAL `\n` (ters bölü + n) İKİSİNİ de ayraç sayar.
 * Sebebi veri: aynı alan iki farklı biçimde saklanmış durumda — özgün İngilizce
 * seed gerçek satır sonu kullanıyor, sektör sayfaları ve platform alt sayfaları
 * ise literal `\n`. Yalnız gerçek satır sonuna bölündüğü için o sayfalarda
 * bütün maddeler tek satıra çöküyor ve ekranda `\n` GÖRÜNÜYORDU — dört dilde,
 * 31 sektör + 4 platform sayfasında.
 *
 * Düzeltmeyi veride değil burada yapmak bilinçli: CMS'e metin yapıştıran birinin
 * hangi biçimi getireceği kestirilemez, iki biçimi de kabul etmek kalıcı çözüm.
 */
export function splitLines(value?: unknown): string[] {
  // Panelin satır editörü yeni maddeleri DİZİ olarak yazar (kiracı bölümleriyle
  // aynı biçim); eski vitrin verisi metin. İkisi de okunmalı — yalnız metin
  // beklemek, panelden bir madde eklendiği an bölümü `split is not a function`
  // ile düşürürdü.
  if (Array.isArray(value)) {
    return value.map((line) => String(line ?? '').trim()).filter(Boolean);
  }
  if (typeof value !== 'string') return [];
  return value
    .split(/\\n|\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}
